import { useState, useCallback, ReactNode } from 'react';
import Dialog from 'components/Dialog';
import Button from 'components/Button';
import { Grid, Link } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useLanguage } from 'context/AppContext';

type MultiSelectModalProps<T> = {
  open: boolean;
  title?: string;
  heading?: ReactNode;
  body?: ReactNode;
  items: T[];
  idKey?: keyof T;
  labelKey?: keyof T;
  onClose: VoidFunction;
  onConfirm: (selectedItems: T[]) => void | Promise<void>;
  confirmLabel?: string;
  loading?: boolean;
  loadingText?: string;
};

const MultiSelectModal = <T,>({
  open = true,
  title,
  heading = null,
  body = null,
  items,
  idKey = 'id' as keyof T,
  labelKey = 'name' as keyof T,
  onClose,
  onConfirm,
  confirmLabel,
  loading = false,
  loadingText,
}: MultiSelectModalProps<T>) => {
  const lang = useLanguage()[0].MultiSelectModal;

  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const handleSelection = useCallback((item: T) => {
    setSelectedItems(prevSelected =>
      prevSelected.includes(item) ? prevSelected.filter(i => i !== item) : [...prevSelected, item],
    );
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedItems(prevSelected => (prevSelected.length == items.length ? [] : items));
  }, [items]);

  const handleConfirm = useCallback(() => {
    onConfirm(selectedItems);
  }, [onConfirm, selectedItems]);

  const handleClose = useCallback(() => {
    // clear selected items, no rerender if already empty
    setSelectedItems(prevSelected => (prevSelected.length ? [] : prevSelected));
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      title={title || lang.title}
      heading={
        loading ? (
          heading
        ) : (
          <Grid container justifyContent="space-between">
            {heading}
            {items.length > 1 && (
              <Link onClick={toggleSelectAll} sx={{ cursor: 'pointer', fontSize: '0.9em', mt: '0.1em' }}>
                {selectedItems.length == items.length ? lang.deselectAll : lang.selectAll}
              </Link>
            )}
          </Grid>
        )
      }
      body={
        <Grid container direction="column" gap="8px" padding="16px 0 0">
          {loading ? (
            <>{loadingText || lang.pleaseWait}</>
          ) : !items.length ? (
            <>
              {body}
              <>{lang.noItemsFound}</>
            </>
          ) : (
            <>
              {body}
              {items.map(item => {
                const isSelected = selectedItems.includes(item);
                return (
                  <Button
                    startIcon={isSelected ? <CheckBoxIcon /> : undefined}
                    key={item[idKey] as string}
                    label={item[labelKey] as string}
                    colors={isSelected ? 'white on active' : 'active on white'}
                    onClick={() => handleSelection(item)}
                    sx={{
                      width: '100%',
                      '& .MuiButton-icon': {
                        position: 'absolute',
                        left: '9px',
                        top: '5px',
                      },
                    }}
                  />
                );
              })}
            </>
          )}
        </Grid>
      }
      onClose={handleClose}
      onConfirm={handleConfirm}
      confirmLabel={confirmLabel || lang.confirm}
      confirmDisabled={!items.length || !selectedItems.length || loading}
      onCancel={handleClose}
      cancelLabel={lang.close}
    />
  );
};

export default MultiSelectModal;
